// Portado del prototipo del Brain (`dont-merge-front-vision`), con su nombre y
// su codigo: se abre hacia arriba, va en un portal para que el desenfoque cubra
// la pagina entera, y se cierra al pulsar fuera, con Escape o al hacer scroll.
//
// **Los agentes de alli eran inventados**, y llevaban `emoji`, `color` y
// `accentColor` escritos a mano. La API no tiene esos campos, asi que el avatar
// es la inicial sobre un color derivado del nombre: decoracion estable, no un
// dato que nadie ha dicho. El contador «17t» si es real: es `tool_count`.

import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { createPortal } from 'react-dom'
import { AtSign, Brain, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Agent } from './types'
import { agentColor, agentInitial } from './helpers'
import { RotuloDeSeccion } from '@/components/ui/rotulo-de-seccion'

export function AgentSelector({ agents, activeAgent, onSelect, disabled }: {
  /** Los agentes del despliegue. En el prototipo era una constante inventada. */
  agents: Agent[]
  activeAgent: Agent | null
  onSelect: (agent: Agent | null) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<{ left: number; bottom: number; maxHeight: number } | null>(null)

  const POPOVER_WIDTH = 288 // w-72


  // Measure the trigger and place the popover above it (in a body portal so its
  // own backdrop-filter blurs the whole page, not just the input it lives in).
  const reposition = React.useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    // Clamp left so the popover never overflows the right viewport edge.
    const left = Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 12)
    // Cap the height to the space above the trigger, leaving a top margin so it
    // never touches the viewport edge — the inner agent list scrolls instead.
    // Using the real available space (never more) prevents top clipping on short
    // mobile viewports; the header/Brain/footer stay pinned and the list scrolls.
    const topMargin = 16
    const available = r.top - 8 - topMargin
    setCoords({
      left: Math.max(12, left),
      bottom: window.innerHeight - r.top + 8,
      maxHeight: available,
    })
  }, [])

  useEffect(() => {
    if (!open) return
    reposition()
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition])

  const isBrain = !activeAgent

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="xs"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={cn(
          "gap-2 font-medium",
          isBrain ? "text-muted-foreground" : "bg-muted/60 text-foreground"
        )}
        title="Seleccionar agente"
      >
        {/* icon slot */}
        {isBrain ? (
          <AtSign className="h-4 w-4 shrink-0" />
        ) : (
          <span
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-md text-xs font-bold leading-none',
              agentColor(activeAgent.name),
            )}
          >
            {agentInitial(activeAgent)}
          </span>
        )}

        {/* label — hidden on very small inputs */}
        <span className={cn("hidden sm:inline truncate max-w-[96px]", !isBrain && "text-foreground")}>
          {isBrain ? 'Brain' : activeAgent.display_name || activeAgent.name}
        </span>

        <ChevronDown className={cn(
          "h-3 w-3 shrink-0 text-muted-foreground transition-transform",
          open && "rotate-180"
        )} />
      </Button>

      {/* ── Popover — portaled to body so backdrop-blur frosts the whole page.
          Inside the input it would only blur the input's own backdrop layer. ── */}
      {open && coords && createPortal(
        <div
          ref={popoverRef}
          style={{ position: 'fixed', left: coords.left, bottom: coords.bottom, width: POPOVER_WIDTH, maxHeight: coords.maxHeight }}
          className={cn(
            "z-[200] flex flex-col overflow-hidden",
            "rounded-lg border border-border bg-background/40 shadow-md backdrop-blur-2xl backdrop-saturate-150",
            "animate-in fade-in slide-in-from-bottom-2 duration-150"
          )}
        >
          {/* Header — pinned */}
          <div className="shrink-0 border-b border-border px-4 py-3">
            <RotuloDeSeccion>Hablar con</RotuloDeSeccion>
          </div>

          {/* Brain — pinned, never scrolls */}
          <div className="shrink-0 p-1.5 pb-0">
            <Button
              variant="ghost"
              size="tarjeta"
              onClick={() => { onSelect(null); setOpen(false) }}
              className={cn("flex-row items-center gap-2", isBrain && "bg-muted")}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary shadow-md">
                <Brain className="h-4 w-4 text-primary-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">MUVERAN Brain</span>
                  {isBrain && <span className="rounded-full bg-primary/15 px-2 py-px text-xs font-medium text-primary">activo</span>}
                </div>
                <p className="truncate text-xs leading-tight text-muted-foreground mt-0.5">Asistente maestro — acceso a todo</p>
              </div>
            </Button>
            <div className="mx-3 mt-1.5 border-t border-border" />
          </div>

          {/* Agent list — fills remaining height and scrolls */}
          <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto p-1.5 pt-1">
            {agents.map(agent => {
              const isActive = activeAgent?.id === agent.id
              return (
                <Button
                  key={agent.id}
                  variant="ghost"
                  size="tarjeta"
                  onClick={() => { if (agent.is_enabled !== false) { onSelect(agent); setOpen(false) } }}
                  disabled={agent.is_enabled === false}
                  className={cn("flex-row items-center gap-2", isActive && "bg-muted")}
                >
                  {/* Avatar */}
                  <div className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold shadow-sm",
                    agentColor(agent.name),
                    agent.is_enabled === false && "grayscale"
                  )}>
                    {agentInitial(agent)}
                  </div>

                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{agent.display_name || agent.name}</span>
                      {isActive && (
                        <span className="rounded-full bg-muted px-2 py-px text-xs font-medium text-primary">activo</span>
                      )}
                      {agent.is_enabled === false && (
                        <span className="rounded-full bg-muted/60 px-2 py-px text-xs font-medium text-muted-foreground">desactivado</span>
                      )}
                    </div>
                    <p className="truncate text-xs leading-tight text-muted-foreground mt-0.5">{agent.description}</p>
                  </div>

                  {/* Tools count */}
                  <span className="shrink-0 rounded-lg bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
                    {agent.tool_count ?? 0}t
                  </span>
                </Button>
              )
            })}
          </div>

          {/* Footer hint — pinned */}
          <div className="shrink-0 border-t border-border px-4 py-2.5">
            <p className="text-xs text-muted-foreground">
              También puedes escribir <kbd className="rounded-md bg-muted px-1.5 py-px font-mono text-xs text-muted-foreground">@nombre</kbd> en el mensaje
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

/* ─── Chat input ─────────────────────────────────────────── */
